/**
 * VÁRIAS LINHAS NO MESMO CPF = UM TITULAR PÓS + DEPENDENTES (Douglas 29/09/2026)
 *
 * "ele não pode comprar duas linhas de 40GB 49,90, o sistema não permite. Então
 *  teremos que vender o plano com dependentes."
 *
 * Régua do book Claro Residencial de 10/09/2026 (p. 6, 10–12, 27, 30, 35, 48, 59):
 * - o mesmo plano não entra duas vezes como titular no mesmo CPF;
 * - cada Pós leva um número de dependentes — os incluídos (R$ 0,00) primeiro,
 *   depois os pagos (R$ 55,00 voz + dados; R$ 29,99 só dados), até o máximo do plano;
 * - Controle não tem linha adicional em nenhuma tabela: não leva dependente;
 * - um Multi por CPF (Douglas 29/09/2026): só UMA linha titular no preço Multi.
 *
 * Os números saem do catálogo (planos_proposta_catalogo.metadata), nunca daqui.
 * Uma função só para a tela, a Conecta e a criação do pedido: com três contas,
 * elas divergem no primeiro ajuste.
 */

export type TipoDependente = 'incluso' | 'pago' | 'dados';

export interface MetaFamiliaCpf {
  familia_movel?: 'POS' | 'CONTROLE' | 'DEPENDENTE' | string;
  plano_claro?: string;
  dependentes_inclusos?: number;
  dependentes_pagos_max?: number;
  dependente?: TipoDependente | string;
  modalidade?: string;
  plano_base?: string;
  combo_movel_plano_base?: string;
  combo_codigo?: string;
  combo_total?: number | string;
}

export interface LinhaFamiliaCpf {
  id: string;
  linha_id?: string;
  numero?: string;
  tipo: string;
  titularLinhaId?: string;
  plano?: { nome?: string } | null;
}

const MOVEIS = new Set(['PORTABILIDADE', 'TT', 'TT_PRE', 'NOVA', 'LINHA_NOVA', 'RENOVACAO']);

export const chaveLinha = (l: { id: string; linha_id?: string }) => l.linha_id || l.id;

const nomeDaLinha = (l: LinhaFamiliaCpf) => {
  const n = String(l.numero ?? '').replace(/\D/g, '');
  return n.length >= 10 ? `(${n.slice(0, 2)}) ${n.slice(2)}` : (l.plano?.nome || 'linha nova');
};

export interface FamiliaConferida {
  erros: string[];
  /** o tipo que cada dependente TEM que ter, pela ordem da proposta */
  tipoEsperado: Map<string, TipoDependente>;
  /** dependentes de cada titular */
  dependentesDe: Map<string, LinhaFamiliaCpf[]>;
}

export function conferirFamiliaCpf(
  linhas: LinhaFamiliaCpf[],
  metaDe: (linha: LinhaFamiliaCpf) => MetaFamiliaCpf | null | undefined,
): FamiliaConferida {
  const erros: string[] = [];
  const tipoEsperado = new Map<string, TipoDependente>();
  const dependentesDe = new Map<string, LinhaFamiliaCpf[]>();
  const moveis = linhas.filter((l) => MOVEIS.has(l.tipo));
  const titulares = new Map<string, { linha: LinhaFamiliaCpf; meta: MetaFamiliaCpf }>();

  const porPlano = new Map<string, LinhaFamiliaCpf[]>();
  for (const l of moveis) {
    const meta = metaDe(l);
    if (!meta || meta.familia_movel === 'DEPENDENTE') continue;
    if (meta.familia_movel !== 'POS' && meta.familia_movel !== 'CONTROLE') continue;
    titulares.set(chaveLinha(l), { linha: l, meta });
    const chave = meta.plano_claro || l.plano?.nome || '';
    if (!chave) continue;
    porPlano.set(chave, [...(porPlano.get(chave) ?? []), l]);
  }
  const noMulti = [...titulares.values()].filter(({ meta }) => String(meta.modalidade ?? '').toUpperCase() === 'NO MULTI');
  if (noMulti.length > 1) {
    erros.push(`${noMulti.length} linhas estão no preço Multi, e o CPF tem um Multi só. Deixe uma linha como titular no Multi `
      + 'e ponha as outras pessoas como dependentes dela (o titular precisa ser um Pós).');
  }
  for (const [plano, iguais] of porPlano) {
    if (iguais.length < 2) continue;
    erros.push(`O plano ${plano} aparece em ${iguais.length} linhas. No mesmo CPF a Claro não aceita o mesmo plano duas vezes: `
      + 'deixe uma linha como titular e ponha as outras pessoas como dependentes de um plano Pós.');
  }

  for (const l of moveis) {
    const meta = metaDe(l);
    if (meta?.familia_movel !== 'DEPENDENTE') continue;
    const titular = l.titularLinhaId ? titulares.get(l.titularLinhaId) : undefined;
    if (!titular) {
      erros.push(`A linha ${nomeDaLinha(l)} é dependente e precisa estar ligada a um titular Pós desta proposta.`);
      continue;
    }
    dependentesDe.set(chaveLinha(titular.linha), [...(dependentesDe.get(chaveLinha(titular.linha)) ?? []), l]);
  }

  for (const [chave, deps] of dependentesDe) {
    const { linha, meta } = titulares.get(chave)!;
    if (meta.familia_movel === 'CONTROLE') {
      erros.push(`${linha.plano?.nome || 'O Controle'} não leva dependente: nenhuma tabela de Controle da Claro tem linha adicional. `
        + 'Para dividir a internet, o titular precisa ser um Pós.');
      continue;
    }
    const inclusos = Number(meta.dependentes_inclusos ?? 0);
    const max = inclusos + Number(meta.dependentes_pagos_max ?? 0);
    if (deps.length > max) {
      erros.push(`${linha.plano?.nome || 'Este Pós'} leva no máximo ${max} dependente(s) (${inclusos} incluído(s) e `
        + `${max - inclusos} pago(s)); a proposta tem ${deps.length}. Escolha um Pós maior ou tire uma linha.`);
      continue;
    }
    let usados = 0;
    for (const d of deps) {
      if (metaDe(d)?.dependente === 'dados') { tipoEsperado.set(chaveLinha(d), 'dados'); continue; }
      tipoEsperado.set(chaveLinha(d), usados < inclusos ? 'incluso' : 'pago');
      usados += 1;
    }
  }

  for (const l of moveis) {
    const esperado = tipoEsperado.get(chaveLinha(l));
    const tem = metaDe(l)?.dependente;
    if (esperado && tem && esperado !== tem) {
      erros.push(`O dependente ${nomeDaLinha(l)} está como ${tem === 'incluso' ? 'incluído' : tem}, mas pela ordem do plano ele é `
        + `${esperado === 'incluso' ? 'incluído (R$ 0,00)' : 'pago'}. Refaça os dependentes do titular.`);
    }
  }
  for (const l of linhas) {
    const exige = metaDe(l)?.combo_movel_plano_base;
    if (!exige) continue;
    const temPar = moveis.some((m) => metaDe(m)?.plano_base === exige && metaDe(m)?.familia_movel !== 'DEPENDENTE');
    if (!temPar) {
      erros.push(`${l.plano?.nome || 'Esta fibra'} é o combo ${metaDe(l)?.combo_codigo ?? ''} e só existe junto com o Pós 60 GB no Multi `
        + `(a 600 Mega + o Pós 60 GB por R$ ${Number(metaDe(l)?.combo_total ?? 99.99).toFixed(2).replace('.', ',')}). Ponha o celular no Pós 60 GB ou escolha a fibra 600 da praça.`);
    }
  }
  return { erros, tipoEsperado, dependentesDe };
}

/** Quantos dependentes ainda cabem no titular (incluídos livres e pagos livres). */
export function vagasDoTitular(meta: MetaFamiliaCpf | null | undefined, dependentesAtuais: number) {
  if (!meta || meta.familia_movel !== 'POS') return { inclusos: 0, pagos: 0, total: 0 };
  const inc = Number(meta.dependentes_inclusos ?? 0);
  const pag = Number(meta.dependentes_pagos_max ?? 0);
  const inclusosLivres = Math.max(inc - dependentesAtuais, 0);
  const pagosLivres = Math.max(pag - Math.max(dependentesAtuais - inc, 0), 0);
  return { inclusos: inclusosLivres, pagos: pagosLivres, total: inclusosLivres + pagosLivres };
}
